// HomeCardList.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { fetchHomeCards, editHomeCard, updateHomeCard } from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const HomeCardList = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const res = await fetchHomeCards();
      setCards(res.data || []);
    } catch (err) {
      toast.error("Failed to fetch Home Cards");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await editHomeCard(id);
      setEditItem(res.data);
    } catch (err) {
      toast.error("Failed to load card data for editing.");
    }
  };

  const handleSaveEdit = async () => {
    if (!editItem.heading.trim() || !editItem.sub_heading.trim()) {
      return toast.error("Heading and Subheading are required");
    }

    setLoadingAction(true);
    const formData = new FormData();
    formData.append("heading", editItem.heading);
    formData.append("sub_heading", editItem.sub_heading);
    formData.append("redirect_link", editItem.redirect_link || "");
    formData.append("order", editItem.order || 0);

    if (selectedIcon) {
      formData.append("icon", selectedIcon);
    }

    try {
      await updateHomeCard(editItem.id, formData);
      toast.success("Home Card updated successfully!");
      fetchCards();
      setEditItem(null);
    } catch (err) {
      toast.error("Failed to save changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "heading", headerName: "Heading", width: 200 },
    { field: "sub_heading", headerName: "Subheading", width: 200 },
    {
      field: "icon",
      headerName: "Icon",
      width: 100,
      renderCell: (params) =>
        params.row.icon ? (
          <img
            src={`${baseImage_path}/${params.row.icon}`}
            alt="icon"
            style={{ width: 50, height: 50, objectFit: "cover" }}
          />
        ) : (
          "No Icon"
        ),
    },
    { field: "redirect_link", headerName: "Redirect Link", width: 200 },
    { field: "order", headerName: "Order", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleEdit(params.row.id)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Home Cards List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={cards}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
        />
      )}

      {editItem && (
        <Dialog open onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Home Card</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editItem.heading}
              onChange={(e) =>
                setEditItem({ ...editItem, heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Subheading"
              value={editItem.sub_heading}
              onChange={(e) =>
                setEditItem({ ...editItem, sub_heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Redirect Link"
              value={editItem.redirect_link || ""}
              onChange={(e) =>
                setEditItem({ ...editItem, redirect_link: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Order"
              type="number"
              value={editItem.order || 0}
              onChange={(e) =>
                setEditItem({ ...editItem, order: e.target.value })
              }
            />

            <Box mt={2}>
              <Typography>Upload Icon</Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedIcon(e.target.files[0])}
              />
              {editItem.icon && !selectedIcon && (
                <img
                  src={`${baseImage_path}/${editItem.icon}`}
                  alt="Current Icon"
                  style={{ marginTop: 10, width: 100, height: 100, objectFit: "cover" }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default HomeCardList;